import React, { useState, useEffect, Fragment } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { Col, Row, Form, message, Empty, Radio, Typography } from "antd";
import { useParams } from "react-router-dom";

import { getBookingDetail } from "../../../ducks/actions";
import { ActionModal } from "../ActionModal";
import { InputNumberFieldV2 as InputNumberField } from "../../../../../atoms/FormElement/InputNumberField";
import { useLeaseTermAmendments } from "../api/v1/useLeaseTermAmendments";
import { numberWithCommas } from "../../utils";

var _ = require("lodash");

type LeaseActionModalProps = {
  isOpen: boolean;
  onClose: () => void;
};
type Step = "confirmation" | "finalization";
let initialState = {
  paymentId: "",
  visible: false,
  paymentPlan: [],
  selectedIndex: null,
  isPlanChanged: false,
};
export const PaymentPlanAmendment = (props: LeaseActionModalProps) => {
  const { isOpen, onClose } = props;
  // hooks initialization
  let [{ visible, paymentId, paymentPlan, isPlanChanged, selectedIndex }, setState] = useState(initialState);
  const updateState = (state: {}) => setState((prevState: any) => ({ ...prevState, ...state }));
  const dispatch: any = useDispatch();
  const { leaseId = "" } = useParams();
  const { loading, error, data, submit } = useLeaseTermAmendments("booking/leaseAmendments");
  // redux states
  const { bookingDetails } = useSelector((state: any) => state.newBooking);

  const {
    control,
    setValue,
    getValues,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm();
  const { Text } = Typography;
  const [step, setStep] = useState<Step>("confirmation");

  const onSubmit = handleSubmit(async (data) => {
    if (paymentPlan?.length === 0) return false;
    try {
      let payload = {};
      let formData = {};

      // find index of the payment plan by paymentPlanId
      const findSelectedItem = paymentPlan?.findIndex((item) => item?.paymentPlanId === paymentId);

      for (let key in data) {
        const splitKey = key.split("-");

        if (findSelectedItem === parseInt(splitKey[1])) {
          const val = data[key].toString();
          if (val.includes(",")) {
            payload[splitKey[0]] = parseInt(val.replace(/,/g, ""));
          } else {
            payload[splitKey[0]] = parseInt(val);
          }

          payload["installments"] = paymentPlan[findSelectedItem].installments;
        }
      }

      // attach old values (for comparison / approval)
      const oldPlan = paymentPlan?.find((p) => p.paymentPlanId === bookingDetails?.tenantDetails?.paymentPlanId);
      if (oldPlan) {
        payload["oldNoOfYear"] = oldPlan.noOfYear;
        payload["oldAnnualRent"] = oldPlan.annualRent;
        payload["oldSecurityDeposit"] = oldPlan.securityDeposit;
      }

      // update paymentPlan array based on paymentPlanId (not selectedIndex)
      const copy = paymentPlan.map((plan) => (plan.paymentPlanId === paymentId ? { ...plan, ...payload } : plan));

      formData = {
        leaseId: leaseId,
        paymentPlan: copy,
        paymentPlanId: paymentId,
      };
      const response = await submit(formData);
      if (response?.code === 200) {
        if (leaseId) dispatch(getBookingDetail(leaseId));
        message.success("updated sucessfully");
        onClose();
      }
    } catch (error) {
      message.error(error.response?.data?.error || error.response?.data?.message);
    }
  });

  const handleOk = (data?) => {
    if (step === "confirmation") setStep("finalization");
    else onSubmit(data);
  };

  const resetForm = () => {
    onClose();
    setStep("confirmation");
    reset();
  };

  useEffect(() => {
    updateState({
      paymentPlan: bookingDetails.paymentPlan ? bookingDetails.paymentPlan : [],
      paymentId: bookingDetails?.tenantDetails?.paymentPlanId,
      selectedIndex: bookingDetails?.paymentPlan?.findIndex(
        (payment: any) => payment?.paymentPlanId === bookingDetails?.tenantDetails?.paymentPlanId
      ),
    });
  }, []);

  return (
    <ActionModal
      isOpen={isOpen}
      onClose={onClose}
      title="Payment Plan Amendments"
      steps={step === "confirmation" ? "Confirm" : "Submit"}
      handleOk={handleOk}
      resetForm={resetForm}
      confirmLoading={loading}
      width={step === "confirmation" ? 600 : 1200}
    >
      {paymentPlan?.length === 0 && (
        <Row style={{ justifyContent: "center" }}>
          <Empty style={{ alignContent: "center" }} />
        </Row>
      )}
      {step === "confirmation" && paymentPlan?.length > 0 ? (
        <div>
          <p>Please confirm if you want to update the payment plan.</p>
          <p>Once updated, it can impact or update annual rent and number of installments.</p>
          <p>Are you sure you want to proceed?</p>
        </div>
      ) : (
        <Form
          layout="vertical"
          name="bookingform"
          scrollToFirstError
          onFinish={handleSubmit(onSubmit)}
          className="bf-main view-booking edit-payment-schedule"
        >
          {paymentPlan?.length > 0 && (
            <>
              {
                <Row gutter={[24, 24]} style={{ letterSpacing: "1.2px" }}>
                  <Col span={12}>
                    <Text className="fontWeight500 ag-default mb-1">Number of Payment</Text>
                  </Col>
                  <Col span={4}>
                    <Text className="fontWeight500 ag-default mb-2">Annual Rent</Text>
                  </Col>
                  {
                    <Col span={4}>
                      <Text className="fontWeight500 ag-default mb-2">Number of Years</Text>
                    </Col>
                  }
                  <Col span={4}>
                    <Text className="fontWeight500 ag-default mb-2">Security Deposit</Text>
                  </Col>
                </Row>
              }
            </>
          )}

          {paymentPlan.map((item: any, index: number) => {
            return (
              <Fragment key={item?.unitPaymentPlanId}>
                <Row gutter={24} style={{ position: "relative" }}>
                  <Col span={12} style={index === 0 ? { marginTop: "10px" } : {}}>
                    {
                      <Radio
                        value={item?.installments}
                        checked={paymentId === item?.paymentPlanId}
                        onChange={() =>
                          updateState({
                            paymentId: item?.paymentPlanId,
                            selectedIndex: index,
                            isPlanChanged: true,
                          })
                        }
                      >
                        {" "}
                        {item?.isCustomPaymentPlan
                          ? `${item?.installments} Cheques (Custom)`
                          : `${item?.installments} Cheques`}
                      </Radio>
                    }
                  </Col>

                  <Col span={4}>
                    <InputNumberField
                      control={control}
                      isRequired={true}
                      format="currency"
                      inputNumberProps={{ min: 0 }}
                      rules={{ required: "required" }}
                      fieldname={`annualRent-${index}`}
                      defaultValue={paymentPlan?.length > 0 ? Number(item?.annualRent || 0) : 0}
                    />
                  </Col>

                  {
                    <Col span={4}>
                      <InputNumberField
                        disabled={true}
                        control={control}
                        // isRequired={true}
                        defaultValue={item?.noOfYear}
                        fieldname={`noOfYear-${index}`}
                        // rules={{ required: "required" }}
                      />
                    </Col>
                  }

                  <Col span={4}>
                    <InputNumberField
                      control={control}
                      disabled={true}
                      format="currency"
                      //   isRequired={true}
                      rules={{ required: "required" }}
                      fieldname={`securityDeposit-${index}`}
                      defaultValue={Number(item?.securityDeposit || 0)}
                    />
                  </Col>
                </Row>
              </Fragment>
            );
          })}
        </Form>
      )}
    </ActionModal>
  );
};
